import { EmailAddressesService, PersonalAddress } from "client/openapi";
import { useEffect, useState } from "react";

/**
 * The signed-in admin's own personal address in each organization, fetched
 * once for every reply box, and replaced when Personal Settings loads or
 * saves it.
 */
const known = new Map<number, Promise<PersonalAddress | undefined>>();

/** An address was just confirmed (its link was opened): fetch them again. */
export function forgetPersonalAddresses() {
  known.clear();
}

/** Personal Settings has the address as saved: reply boxes use it from now on. */
export function rememberPersonalAddress(
  orgId: number,
  address: PersonalAddress
) {
  known.set(orgId, Promise.resolve(address));
}

/**
 * Where answers to the admin's replies come back when they go as their
 * personal address (from it, or with Reply-To it): that address. Undefined
 * while it loads, when they have none confirmed, or when Tadpole doesn't use
 * it. `wanted` is false for anyone but an admin.
 */
export function usePersonalReplyAddress(
  orgId: number,
  wanted: boolean
): string | undefined {
  const [address, setAddress] = useState<PersonalAddress>();
  useEffect(() => {
    if (!wanted) return;
    let current = true;
    if (!known.has(orgId)) {
      known.set(
        orgId,
        EmailAddressesService.getPersonalAddress({ orgId }).catch(
          () => undefined
        )
      );
    }
    known.get(orgId)?.then((found) => current && setAddress(found));
    return () => {
      current = false;
    };
  }, [orgId, wanted]);
  if (!wanted || !address?.confirmed || !address.email) return undefined;
  const used =
    address.sends_from === address.email || address.reply_to === address.email;
  return used ? address.email : undefined;
}
